import { useQuery } from '@hooks/reactQuery';

import { getKPIAlerts, getKPIAlertsForRan } from '@api/kpiAlerts';

import { extractQueryParams, normalizeObject } from '@utils/common';

import { IGetKpiAlertsParamsDTO, IKpiAlertDTO } from '@interfaces/kpi/dto/kpiAlertsDTO';

import { EVendorsValues, TVendorsValues } from '@enums/common';

import { KPI_ALERTS, KPI_ALERTS_ORAN } from '@constants/queryKeys';

export interface IUseKPIAlertsParams extends Omit<IGetKpiAlertsParamsDTO, 'severity' | 'isActive'> {
	severity: string | null;
	isActive: boolean | null;
	vendor: TVendorsValues | null;
}

export interface IUseKPIAlertsResponse {
	kpiAlerts: IKpiAlertDTO[];
	totalPages: number;
	totalElements: number;
	isLoading: boolean;
}

export function useKPIAlerts(params: IUseKPIAlertsParams): IUseKPIAlertsResponse {
	const { pageSize = 10, vendor } = params;

	const normalizedParams = normalizeObject(params);

	const {
		data: dataForLimenet,
		isInitialLoading: isFetchingForLimenet,
	} = useQuery<{
		items: IKpiAlertDTO[];
		count: number;
	}>({
		queryKey: [KPI_ALERTS, normalizedParams],
		queryFn: extractQueryParams(getKPIAlerts),
		refetchOnWindowFocus: false,
		enabled: [vendor !== EVendorsValues.oran],
	});

	const {
		data: dataForRan,
		isInitialLoading: isFetchingForRan,
	} = useQuery<{
		items: IKpiAlertDTO[];
		count: number;
	}>({
		queryKey: [KPI_ALERTS_ORAN, normalizedParams],
		queryFn: extractQueryParams(getKPIAlertsForRan),
		refetchOnWindowFocus: false,
		enabled: [vendor === EVendorsValues.oran],
	});

	const data = vendor === EVendorsValues.oran ? dataForRan : dataForLimenet;

	return {
		kpiAlerts: data?.items || [],
		totalPages: Math.ceil((data?.count || 0) / pageSize),
		totalElements: data?.count || 0,
		isLoading: isFetchingForRan || isFetchingForLimenet,
	};
}
